import React, { useState, useEffect, useRef } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Status → friendly label + pill class. Note: in practice the lifecycle is
// queued → complete/failed (the webhook only sets those two), so 'queued'
// is the real in-flight state and we surface it as "Processing".
const STATUS_META = {
  queued:     { label: 'Processing', cls: 'pill-processing' },
  generating: { label: 'Generating', cls: 'pill-processing' },
  rendering:  { label: 'Rendering',  cls: 'pill-processing' },
  complete:   { label: 'Complete',   cls: 'pill-complete' },
  failed:     { label: 'Failed',     cls: 'pill-failed' },
};

// Re-run is blocked server-side (409) while a job is genuinely mid-flight on
// Railway. Mirror that here so the button reflects reality.
const RERUNNABLE = (status) => status !== 'generating' && status !== 'rendering';

// Add Cloudinary's fl_attachment flag so the link downloads instead of playing.
const toDownloadUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('/fl_attachment')) return url;
  return url.replace('/upload/', '/upload/fl_attachment/');
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ', ' + dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || '?';

const VideoJobsManager = () => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination — server-driven
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const jobsPerPage = 25;
  const searchTimeoutRef = useRef(null);

  // Per-row interaction state
  const [rerunningId, setRerunningId] = useState(null);   // job currently dispatching
  const [confirmingId, setConfirmingId] = useState(null); // job showing inline confirm

  // Preview modal
  const [previewJob, setPreviewJob] = useState(null);

  // ── Data ────────────────────────────────────────────────────────────
  const fetchJobs = async (page = 1, search = '', status = 'all') => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: jobsPerPage.toString(),
      });
      if (search.trim()) params.append('search', search.trim());
      if (status && status !== 'all') params.append('status', status);

      const response = await fetch(`${API_URL}/admin/video-jobs?${params}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` },
      });
      const data = await response.json();

      if (response.ok) {
        setJobs(data.jobs || []);
        setCurrentPage(data.pagination?.page || 1);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalJobs(data.pagination?.total || (data.jobs ? data.jobs.length : 0));
      } else {
        setError(data.message || 'Failed to fetch video jobs');
      }
    } catch (err) {
      setError('Error fetching video jobs: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs(1, '', 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchJobs(1, term, statusFilter);
    }, 300);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    fetchJobs(1, searchTerm, status);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      fetchJobs(newPage, searchTerm, statusFilter);
    }
  };

  // ── Re-run ──────────────────────────────────────────────────────────
  const handleRerun = async (jobId) => {
    setRerunningId(jobId);
    setConfirmingId(null);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_URL}/admin/video-jobs/${jobId}/rerun`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess('Re-run dispatched — the job is processing again.');
        fetchJobs(currentPage, searchTerm, statusFilter);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to re-run job');
      }
    } catch (err) {
      setError('Error re-running job: ' + err.message);
    } finally {
      setRerunningId(null);
    }
  };

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Video Jobs — Control Center design system (matches UserAssignmentManager) */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .admin-panel .card-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 16px;
        }
        .admin-panel .card-header h3 {
          margin: 0; font-size: 11px; font-weight: 600; color: #9ca3af;
          letter-spacing: 0.06em; text-transform: uppercase;
        }

        .admin-panel .search-container {
          background: #fff; border-radius: 12px; padding: 12px 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04); border: 1px solid #e5e7eb;
          margin-bottom: 20px; display: flex; align-items: center; gap: 12px;
        }
        .admin-panel .search-input {
          flex: 1; border: none; outline: none; font-size: 13px; color: #111;
          background: transparent; font-family: inherit; padding: 0;
        }
        .admin-panel .search-input::placeholder { color: #9ca3af; }

        .admin-panel .alert-success {
          background-color: rgba(34, 197, 94, 0.08); color: #166534;
          border: 1px solid rgba(34, 197, 94, 0.25); padding: 11px 14px;
          border-radius: 10px; margin-bottom: 16px; font-size: 13px; font-weight: 500;
        }
        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08); color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25); padding: 11px 14px;
          border-radius: 10px; margin-bottom: 16px; font-size: 13px; font-weight: 500;
        }

        .admin-panel .dashboard-card .table-container {
          margin: 0 -20px -20px; overflow-x: auto; overflow-y: auto; max-height: 580px;
        }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar { width: 8px; height: 8px; }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-track { background: transparent; }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
        .admin-panel .dashboard-card .table-container::-webkit-scrollbar-thumb:hover { background: #d1d5db; }

        .admin-panel .data-table {
          width: 100%; border-collapse: collapse; background: #fff;
          border: none; border-top: 1px solid #f0f0f0;
        }
        .admin-panel .data-table thead th {
          position: sticky; top: 0; z-index: 2; background: #fafafa;
          padding: 11px 14px; text-align: left; font-weight: 700; font-size: 10px;
          color: #9ca3af; text-transform: uppercase; letter-spacing: 0.08em;
          border-bottom: 1px solid #f0f0f0;
        }
        .admin-panel .data-table td {
          padding: 12px 14px; border-bottom: 1px solid #f5f5f5; color: #111;
          font-size: 13px; text-align: left; vertical-align: middle;
        }
        .admin-panel .data-table tr:last-child td { border-bottom: none; }
        .admin-panel .data-table tbody tr:hover { background: #fafafa; }

        .admin-panel .user-cell { display: flex; align-items: center; gap: 12px; text-align: left; }
        .admin-panel .user-avatar {
          width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0; overflow: hidden;
          background: #eef2ff; color: #4f46e5; display: flex; align-items: center;
          justify-content: center; font-size: 12px; font-weight: 700;
        }
        .admin-panel .user-info { display: flex; flex-direction: column; min-width: 0; text-align: left; align-items: flex-start; }
        .admin-panel .user-name {
          font-size: 13px; font-weight: 600; color: #111; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis; max-width: 220px;
        }
        .admin-panel .user-email {
          font-size: 11px; color: #6b7280; white-space: nowrap; overflow: hidden;
          text-overflow: ellipsis; max-width: 220px;
        }
        .admin-panel .listing-address { font-weight: 500; color: #111; }
        .admin-panel .listing-csz { font-size: 11px; color: #6b7280; }

        .admin-panel .pill {
          display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 10px;
          font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap;
        }
        .admin-panel .pill-processing { background: rgba(245, 158, 11, 0.14); color: #b45309; }
        .admin-panel .pill-complete   { background: rgba(16, 185, 129, 0.12); color: #047857; }
        .admin-panel .pill-failed     { background: rgba(220, 38, 38, 0.1);  color: #b91c1c; }

        .admin-panel .chip-row { display: flex; flex-wrap: wrap; gap: 4px; }
        .admin-panel .chip {
          display: inline-block; padding: 2px 7px; border-radius: 6px; font-size: 10px;
          font-weight: 600; background: #f3f4f6; color: #6b7280; white-space: nowrap;
        }
        .admin-panel a.chip-link {
          background: rgba(37, 99, 235, 0.1); color: #1d4ed8; text-decoration: none;
        }
        .admin-panel a.chip-link:hover { background: rgba(37, 99, 235, 0.18); }

        .admin-panel .muted { color: #9ca3af; }
        .admin-panel .points-refunded { color: #b45309; font-size: 10px; font-weight: 700; }

        .admin-panel .row-actions { display: flex; gap: 6px; align-items: center; }
        .admin-panel .row-action-btn {
          padding: 5px 10px; border-radius: 7px; border: 1px solid #e5e7eb; background: #fff;
          color: #6b7280; font-size: 11px; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 4px; transition: all 0.15s;
          font-family: inherit; white-space: nowrap;
        }
        .admin-panel .row-action-btn:hover:not(:disabled) {
          color: #2563eb; border-color: #2563eb; background: rgba(37, 99, 235, 0.04);
        }
        .admin-panel .row-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .admin-panel .row-action-btn.danger { color: #fff; background: linear-gradient(135deg, #2563eb, #1d4ed8); border: none; }
        .admin-panel .row-action-btn.danger:hover:not(:disabled) { background: linear-gradient(135deg, #1d4ed8, #1e40af); color: #fff; }
        .admin-panel .row-action-btn.ghost { color: #9ca3af; }

        .admin-panel .pagination-container {
          display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 20px;
        }
        .admin-panel .pagination-btn {
          padding: 7px 14px; background: #fff; border: 1px solid #e5e7eb; border-radius: 9px;
          color: #6b7280; font-weight: 600; font-size: 12px; cursor: pointer;
          transition: all 0.15s; font-family: inherit;
        }
        .admin-panel .pagination-btn:hover:not(:disabled) { background: #f9fafb; border-color: #2563eb; color: #2563eb; }
        .admin-panel .pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .admin-panel .page-info { padding: 7px 12px; color: #9ca3af; font-size: 12px; font-weight: 500; }

        .admin-panel .empty-state, .admin-panel .loading-state {
          text-align: center; padding: 48px 20px; color: #9ca3af; font-size: 13px;
        }
        .admin-panel .loading-state .spinner {
          width: 28px; height: 28px; border: 3px solid #f0f0f0; border-top-color: #2563eb;
          border-radius: 50%; margin: 0 auto 12px; animation: vj-spin 0.8s linear infinite;
        }
        @keyframes vj-spin { to { transform: rotate(360deg); } }

        .admin-panel .filter-select {
          padding: 5px 28px 5px 10px; border-radius: 7px; border: 1px solid #e5e7eb;
          background: #fff; font-size: 11px; font-weight: 600; cursor: pointer; outline: none;
          appearance: none; -webkit-appearance: none; -moz-appearance: none; letter-spacing: 0.02em;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 8px center;
        }

        /* Modal */
        .admin-panel .modal-overlay {
          position: fixed; inset: 0; background: rgba(17, 17, 17, 0.5);
          backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; z-index: 1000;
        }
        .admin-panel .modal-content {
          background: #fff; border-radius: 16px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          width: 720px; max-width: 94vw; max-height: 90vh; overflow: hidden;
          display: flex; flex-direction: column;
        }
        .admin-panel .modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; border-bottom: 1px solid #f0f0f0;
        }
        .admin-panel .modal-header h3 { margin: 0; font-size: 16px; font-weight: 700; color: #111; }
        .admin-panel .modal-close {
          background: none; border: none; cursor: pointer; padding: 6px 10px;
          border-radius: 8px; color: #9ca3af; font-size: 20px; line-height: 1;
        }
        .admin-panel .modal-close:hover { background: #f3f4f6; color: #111; }
        .admin-panel .modal-body { padding: 24px; overflow-y: auto; flex: 1; background: #fff; }

        .admin-panel .meta-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 12px 24px; margin-bottom: 20px;
        }
        .admin-panel .meta-item { display: flex; flex-direction: column; gap: 2px; }
        .admin-panel .meta-label {
          font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.06em;
        }
        .admin-panel .meta-value { font-size: 13px; color: #111; }

        .admin-panel .video-grid { display: flex; flex-wrap: wrap; gap: 16px; }
        .admin-panel .video-tile { width: 200px; }
        .admin-panel .video-tile video {
          width: 100%; border-radius: 10px; background: #000; display: block; border: 1px solid #e5e7eb;
        }
        .admin-panel .video-tile .video-label {
          display: flex; align-items: center; justify-content: space-between; margin-top: 6px;
        }
        .admin-panel .video-tile .video-format { font-size: 11px; font-weight: 700; color: #6b7280; }
        .admin-panel .video-tile a.dl {
          font-size: 11px; font-weight: 600; color: #2563eb; text-decoration: none;
        }
        .admin-panel .fail-reason {
          background: rgba(220, 38, 38, 0.06); border: 1px solid rgba(220, 38, 38, 0.2);
          border-radius: 10px; padding: 12px 14px; font-size: 12px; color: #991b1b; margin-bottom: 20px;
        }
      `}</style>

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase' }}>
          Video Jobs
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}
      {success && <div className="alert-success">{success}</div>}

      {/* Search */}
      <div className="search-container">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          placeholder="Search by agent name, email, or listing address..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Table */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>
            All Video Jobs
            <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '600', color: '#d1d5db', letterSpacing: '0.05em' }}>
              {totalJobs}
            </span>
          </h3>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
            style={{ color: statusFilter === 'all' ? '#6b7280' : '#2563eb' }}
            title="Filter by status"
          >
            <option value="all">All Statuses</option>
            <option value="queued">Processing</option>
            <option value="generating">Generating</option>
            <option value="rendering">Rendering</option>
            <option value="complete">Complete</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {(() => {
          if (isLoading) {
            return (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading video jobs...</p>
              </div>
            );
          }
          if (jobs.length === 0) {
            return (
              <div className="empty-state">
                <p>No video jobs found. Try adjusting your search or filter.</p>
              </div>
            );
          }
          return (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: '220px' }}>Agent</th>
                    <th style={{ minWidth: '200px' }}>Listing</th>
                    <th>Status</th>
                    <th>Formats</th>
                    <th>Videos</th>
                    <th>Points</th>
                    <th>Created</th>
                    <th style={{ minWidth: '170px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => {
                    const meta = STATUS_META[job.status] || { label: job.status, cls: 'pill-processing' };
                    const isConfirming = confirmingId === job._id;
                    const isRerunning = rerunningId === job._id;
                    const canRerun = RERUNNABLE(job.status);
                    const outputs = job.outputs || [];

                    return (
                      <tr key={job._id}>
                        {/* Agent */}
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">{initials(job.agent?.name)}</div>
                            <div className="user-info">
                              <span className="user-name">{job.agent?.name || 'Unknown agent'}</span>
                              <span className="user-email">{job.agent?.email || '—'}</span>
                            </div>
                          </div>
                        </td>

                        {/* Listing */}
                        <td>
                          {job.listing ? (
                            <div>
                              <div className="listing-address">{job.listing.address || '—'}</div>
                              {job.listing.csz && <div className="listing-csz">{job.listing.csz}</div>}
                            </div>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>

                        {/* Status */}
                        <td>
                          <span className={`pill ${meta.cls}`}>{meta.label}</span>
                          {job.isRegenerate && (
                            <span className="chip" style={{ marginLeft: 6 }}>regen</span>
                          )}
                        </td>

                        {/* Formats */}
                        <td>
                          <div className="chip-row">
                            {(job.formats || []).length > 0
                              ? job.formats.map((f) => <span key={f} className="chip">{f}</span>)
                              : <span className="muted">—</span>}
                          </div>
                        </td>

                        {/* Videos (links) */}
                        <td>
                          {outputs.length > 0 ? (
                            <div className="chip-row">
                              {outputs.map((o) => (
                                <a
                                  key={o.format}
                                  className="chip chip-link"
                                  href={o.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {o.format} ↗
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>

                        {/* Points */}
                        <td>
                          <div>{job.pointsCost ?? '—'}</div>
                          {job.pointsRefunded && <div className="points-refunded">refunded</div>}
                        </td>

                        {/* Created */}
                        <td style={{ whiteSpace: 'nowrap', color: '#6b7280' }}>{fmtDate(job.createdAt)}</td>

                        {/* Actions */}
                        <td>
                          <div className="row-actions">
                            <button
                              className="row-action-btn"
                              onClick={() => setPreviewJob(job)}
                            >
                              Preview
                            </button>

                            {isConfirming ? (
                              <>
                                <button
                                  className="row-action-btn danger"
                                  disabled={isRerunning}
                                  onClick={() => handleRerun(job._id)}
                                >
                                  {isRerunning ? 'Running…' : 'Confirm'}
                                </button>
                                <button
                                  className="row-action-btn ghost"
                                  onClick={() => setConfirmingId(null)}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className="row-action-btn"
                                disabled={!canRerun || isRerunning}
                                title={canRerun ? 'Re-dispatch this job' : 'Job is mid-render — wait for it to finish'}
                                onClick={() => setConfirmingId(job._id)}
                              >
                                Re-run
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1} title="First page">«</button>
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
          <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} title="Last page">»</button>
        </div>
      )}

      {/* Preview modal */}
      {previewJob && (
        <div className="modal-overlay" onClick={() => setPreviewJob(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{previewJob.listing?.address || 'Video job'}</h3>
              <button onClick={() => setPreviewJob(null)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="meta-grid">
                <div className="meta-item">
                  <span className="meta-label">Agent</span>
                  <span className="meta-value">{previewJob.agent?.name || 'Unknown'}<br />
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{previewJob.agent?.email}</span>
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Status</span>
                  <span className="meta-value">
                    <span className={`pill ${(STATUS_META[previewJob.status] || {}).cls || 'pill-processing'}`}>
                      {(STATUS_META[previewJob.status] || {}).label || previewJob.status}
                    </span>
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Points</span>
                  <span className="meta-value">
                    {previewJob.pointsCost ?? '—'}
                    {previewJob.pointsDeducted ? ' · deducted' : ''}
                    {previewJob.pointsRefunded ? ' · refunded' : ''}
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Created</span>
                  <span className="meta-value">{fmtDate(previewJob.createdAt)}</span>
                </div>
                {previewJob.completedAt && (
                  <div className="meta-item">
                    <span className="meta-label">Completed</span>
                    <span className="meta-value">{fmtDate(previewJob.completedAt)}</span>
                  </div>
                )}
                {previewJob.railwayJobId && (
                  <div className="meta-item">
                    <span className="meta-label">Railway Job ID</span>
                    <span className="meta-value" style={{ fontSize: 11, fontFamily: 'monospace' }}>{previewJob.railwayJobId}</span>
                  </div>
                )}
              </div>

              {previewJob.status === 'failed' && previewJob.failureReason && (
                <div className="fail-reason">
                  <strong>Failure reason:</strong> {previewJob.failureReason}
                </div>
              )}

              {(previewJob.outputs || []).length > 0 ? (
                <div className="video-grid">
                  {previewJob.outputs.map((o) => (
                    <div key={o.format} className="video-tile">
                      <video src={o.url} controls preload="metadata" />
                      <div className="video-label">
                        <span className="video-format">{o.format}</span>
                        <a className="dl" href={toDownloadUrl(o.url)}>Download</a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: '24px 0' }}>
                  <p>No rendered videos yet for this job.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoJobsManager;